import { Dispatch, SetStateAction, useState } from 'react';

import useOnce from './useOnce';

type FetchStateCallback<T> = (arg: Dispatch<SetStateAction<T>>) => void;

/**
 * This hook is a combination of useState and useOnce.
 * During the initial render, it calls a callback function that should fetch the state value.
 * A function to update the state is passed to the callback function as the first argument.
 * The hook returns a stateful value and a function (fetchState) that can re-call the callback.
 */
function useFetchState<T>(
    initialState: Partial<T>,
    callback: FetchStateCallback<T>
): [T, () => void, Dispatch<SetStateAction<T>>] {
    const [state, setState] = useState<T>(initialState as T);
    const fetchState = () => callback(setState);

    useOnce(fetchState);

    return [state, fetchState, setState];
}

export default useFetchState;
